// ===========================================
// Sidebar Component - Main Navigation
// ===========================================

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  DollarSign,         // Dev Costs icon
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Zap,
  SquareCode,         // Dev Prompts icon
  BookOpen,           // Dev Notes icon
  MessageSquare,      // Dev Chat icon
  UserCircle          // Dev Profile icon
} from 'lucide-react';
import './Sidebar.scss';

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items - Main app sections (all under /dashboard)
  const navItems = [
    { path: '/dashboard/profile', icon: UserCircle, label: 'Dev Profile', end: true },    // Developer profile & activity
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dev Projects', end: true },
    { path: '/dashboard/prompts', icon: SquareCode, label: 'Dev Prompts', end: true },    // AI IDE prompt library
    { path: '/dashboard/notes', icon: BookOpen, label: 'Dev Notes', end: true },          // Knowledge notebook
    { path: '/dashboard/analytics', icon: DollarSign, label: 'Dev Costs', end: true },
    { path: '/dashboard/chat', icon: MessageSquare, label: 'Dev Chat', end: true },       // Global dev chat
  ];

  return (
    // Add 'open' class when mobileOpen is true for mobile visibility
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}>
      {/* Logo Section */}
      <div className="sidebar__logo">
        <div className="sidebar__logo-icon">
          <Zap size={24} />
        </div>
        {!collapsed && (
          <div className="sidebar__logo-text">
            <span className="sidebar__logo-title">DevPad</span>
            <span className="sidebar__logo-subtitle">Developer HQ</span>
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button className="sidebar__toggle" onClick={onToggle} aria-label="Toggle sidebar">
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>

      {/* Navigation */}
      <nav className="sidebar__nav">
        <ul className="sidebar__nav-list">
          {navItems.map((item) => (
            <li key={item.path} className="sidebar__nav-item">
              <NavLink
                to={item.path}
                className={({ isActive }) => 
                  `sidebar__nav-link ${isActive ? 'active' : ''}`
                }
                end={item.end}
                onClick={onMobileClose}  // Close sidebar on mobile when navigating
              >
                <item.icon size={20} className="sidebar__nav-icon" />
                {!collapsed && <span className="sidebar__nav-label">{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Section - User & Settings */}
      <div className="sidebar__bottom">
        {/* Settings Link */}
        <NavLink 
          to="/dashboard/settings" 
          className="sidebar__nav-link"
          onClick={onMobileClose}  // Close sidebar on mobile when navigating
        >
          <Settings size={20} className="sidebar__nav-icon" />
          {!collapsed && <span className="sidebar__nav-label">Settings</span>}
        </NavLink>

        {/* User Info */}
        <div className="sidebar__user">
          <div className="sidebar__user-avatar">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="sidebar__user-info">
              <span className="sidebar__user-name">{user?.name}</span>
              <span className="sidebar__user-email">{user?.email}</span>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button className="sidebar__logout" onClick={handleLogout}>
          <LogOut size={20} className="sidebar__nav-icon" />
          {!collapsed && <span className="sidebar__nav-label">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;

